import { useState, type FormEvent } from 'react';
import {
  Anchor,
  Button,
  Container,
  Fieldset,
  Group,
  PasswordInput,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, api, errorMessage } from '../api/client';
import { ErrorNote, ProblemList } from '../components/ErrorNote';
import { MIN_PASSWORD_LENGTH, passwordProblems } from '../auth/password';

/** A link that has expired, been used already, or was mangled by an email client. */
function isTokenTrouble(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return error.status === 400 || error.status === 404 || error.status === 410;
}

function BadLink({ note }: { note: string }) {
  return (
    <Container component="main" size={460} pt={80} pb={64}>
      <Title order={1}>That link did not work</Title>
      <Text size="lg" mt="sm">
        {note}
      </Text>
      <Text mt="md">
        Reset links last one hour and can only be used once. Ask for a fresh one and it will arrive
        in a moment.
      </Text>
      <Group mt="lg">
        <Button component={Link} to="/forgot-password">
          Send me a new link
        </Button>
      </Group>
      <Text c="dimmed" size="sm" mt="lg">
        <Anchor component={Link} to="/login" size="sm">
          Back to sign in
        </Anchor>
      </Text>
    </Container>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [badLink, setBadLink] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  if (token === '') {
    return <BadLink note="The address is missing the part that tells us who you are." />;
  }
  if (badLink) {
    return <BadLink note="It may have expired, or it may have been used already." />;
  }

  if (done) {
    return (
      <Container component="main" size={460} pt={80} pb={64}>
        <Title order={1}>Password changed</Title>
        <Text size="lg" mt="sm">
          We have signed you out everywhere, so sign in again on any device you use quizee on.
        </Text>
        <Group mt="lg">
          <Button component={Link} to="/login">
            Sign in
          </Button>
        </Group>
      </Container>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    const found = passwordProblems(password, confirmation);
    setProblems(found);
    if (found.length > 0) return;

    setBusy(true);
    try {
      await api.confirmPasswordReset(token, password);
      setDone(true);
    } catch (error) {
      if (isTokenTrouble(error)) setBadLink(true);
      else setProblem(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Container component="main" size={460} pt={80} pb={64}>
      <Title order={1}>Set a new password</Title>
      <Text size="lg" mt="sm" mb="lg">
        Choose something you have not used on another site.
      </Text>
      {problem !== null && <ErrorNote message={problem} />}
      <ProblemList problems={problems} />
      <form onSubmit={(event) => void submit(event)}>
        <Fieldset variant="unstyled" disabled={busy}>
          <Stack gap="md">
            <PasswordInput
              id="new-password"
              label="New password"
              description={`At least ${String(MIN_PASSWORD_LENGTH)} characters.`}
              autoComplete="new-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <PasswordInput
              id="confirm-password"
              label="New password again"
              autoComplete="new-password"
              required
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
            <Button type="submit" mt="xs">
              {busy ? 'Saving...' : 'Save the new password'}
            </Button>
          </Stack>
        </Fieldset>
      </form>
    </Container>
  );
}
